import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import Layout from "../layout/Layout";
import { useLoading } from "../context/LoadingContext";

import OurStory from "../assets/About/OurStory.png";
import Roots from "../assets/About/Roots.png";
import Craft from "../assets/About/Craft.png";
import Sustainable from "../assets/About/Sustainable.png";
import Our from "../assets/About/Our.png";

// =====================================================
// Simple reusable wrapper
// Content remains visible even if JavaScript animations
// fail or are disabled.
// =====================================================
const AnimatedSection = ({ children, className = "" }) => (
  <div className={className}>{children}</div>
);

function About() {
  const { setLoading } = useLoading();

  // =====================================================
  // IMPORTANT:
  // Do not keep the artificial 2-second loading delay.
  // The page should be considered loaded immediately.
  // =====================================================
  useEffect(() => {
    setLoading(false);
    window.scrollTo(0, 0);
  }, [setLoading]);

  const base =
    typeof window !== "undefined"
      ? window.location.origin
      : "https://www.shahumumbai.com";

  const url = `${base}/about`;

  return (
    <Layout>
      <Helmet>
        {/* =====================================================
            SEO
        ====================================================== */}

        <title>
          Our Story — Shahu Mumbai | Indian Heritage Luxury Fashion
        </title>

        <meta
          name="description"
          content="Discover the story behind Shahu Mumbai, an Indian heritage luxury fashion house founded by Bhumi Shahu, rooted in India's ancient art, culture, craftsmanship, and Ahimsa silk."
        />

        <meta
          name="robots"
          content="index,follow,max-image-preview:large"
        />

        <meta
          name="keywords"
          content="Shahu Mumbai, Bhumi Shahu, Indian luxury fashion, Indian heritage fashion, Indian silk scarves, Ahimsa silk, Mumbai luxury fashion, Indian craftsmanship, heritage fashion house, sustainable luxury fashion"
        />

        {/* Canonical */}
        <link rel="canonical" href={url} />

        <link rel="alternate" hrefLang="en-IN" href={url} />
        <link rel="alternate" hrefLang="x-default" href={url} />

        {/* =====================================================
            OPEN GRAPH
        ====================================================== */}

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Shahu Mumbai" />
        <meta property="og:locale" content="en_IN" />

        <meta
          property="og:title"
          content="Our Story — Shahu Mumbai | Indian Heritage Luxury Fashion"
        />

        <meta
          property="og:description"
          content="The story of Shahu Mumbai — an Indian heritage luxury fashion house transforming India's forgotten art, ancient stories, cultural traditions, and craftsmanship into modern timeless luxury."
        />

        <meta property="og:url" content={url} />

        <meta
          property="og:image"
          content={`${base}/og/about.jpg`}
        />

        <meta
          property="og:image:alt"
          content="Shahu Mumbai — Indian heritage luxury fashion"
        />

        {/* =====================================================
            STRUCTURED DATA — BREADCRUMB
        ====================================================== */}

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: `${base}/`,
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Our Story",
                item: url,
              },
            ],
          })}
        </script>

        {/* =====================================================
            STRUCTURED DATA — ABOUT PAGE
        ====================================================== */}

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "@id": `${url}#about`,
            name: "Our Story — Shahu Mumbai",
            url: url,
            description:
              "The story behind Shahu Mumbai, an Indian heritage luxury fashion house founded by Bhumi Shahu and rooted in India's artistic heritage, craftsmanship, culture, and Ahimsa silk.",
            about: {
              "@type": "Organization",
              name: "Shahu Mumbai",
              url: base,
            },
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": url,
            },
            isPartOf: {
              "@type": "WebSite",
              name: "Shahu Mumbai",
              url: base,
            },
          })}
        </script>

        {/* =====================================================
            STRUCTURED DATA — ORGANIZATION
        ====================================================== */}

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "@id": `${base}#organization`,
            name: "Shahu Mumbai",
            url: base,
            logo: `${base}/static/logo-300.png`,
            address: {
              "@type": "PostalAddress",
              addressCountry: "IN",
              addressLocality: "Mumbai",
            },
            brand: {
              "@type": "Brand",
              name: "Shahu",
            },
            founder: {
              "@type": "Person",
              name: "Bhumi Shahu",
            },
          })}
        </script>
      </Helmet>

      {/* =====================================================
          PAGE
      ====================================================== */}

      <div className="bg-[#F1E7E5] text-[#3B2E1E]">

        {/* =====================================================
            HERO
        ====================================================== */}

        <section
          className="relative h-[500px] flex items-center justify-center text-center"
          style={{
            backgroundImage: `url(${OurStory})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="absolute inset-0 bg-black/45" />

          <div className="relative z-10 px-6">
            <h1 className="text-4xl md:text-6xl font-bold text-white tracking-wide">
              Our Story
            </h1>

            <p className="mt-5 text-lg md:text-xl text-[#F6E7D8]">
              Our world, curated to be yours.
            </p>
          </div>
        </section>

        {/* =====================================================
            INTRODUCTION
        ====================================================== */}

        <section className="px-6 md:px-12 lg:px-20 py-20">
          <AnimatedSection className="max-w-5xl mx-auto">
            <p className="text-lg md:text-xl leading-relaxed text-[#5F5144]">
              I am <strong>Bhumi Shahu</strong>, and I founded{" "}
              <strong>Shahu Mumbai</strong>, an Indian heritage luxury fashion
              house.
            </p>

            <p className="mt-8 text-base md:text-lg leading-relaxed text-[#6B6B6B]">
              I was born and raised in Mumbai, in a family where fashion
              wasn't a career choice, it was inheritance. For decades, my
              mother and grandmother have worked in garment craftsmanship,
              passing down technique, discipline, and an uncompromising
              respect for quality from one generation to the next.
            </p>

            <p className="mt-6 text-base md:text-lg leading-relaxed text-[#6B6B6B]">
              Shahu is the newest expression of that legacy.
            </p>
          </AnimatedSection>
        </section>

        {/* =====================================================
            WHERE IT BEGAN
        ====================================================== */}

        <section className="grid grid-cols-1 md:grid-cols-2 gap-12 px-6 md:px-12 lg:px-20 py-20 items-center">

          <AnimatedSection>
            <h2 className="text-3xl md:text-4xl font-bold mb-7">
              Where It Began
            </h2>

            <p className="text-base md:text-lg text-[#6B6B6B] leading-relaxed">
              The idea took shape when I moved to New York City for college. I
              found myself surrounded by legacy fashion houses building entire
              identities around their heritage.
            </p>

            <p className="mt-6 text-base md:text-lg text-[#6B6B6B] leading-relaxed">
              But I didn't find one representing India.
            </p>

            <p className="mt-6 text-base md:text-lg text-[#6B6B6B] leading-relaxed">
              That absence stayed with me.
            </p>
          </AnimatedSection>

          <img
            src={Roots}
            alt="The roots and inspiration behind Shahu Mumbai"
            className="rounded-xl h-80 md:h-[420px] w-full object-cover"
            loading="lazy"
          />
        </section>

        {/* =====================================================
            INDIA'S ARTISTIC LEGACY
        ====================================================== */}

        <section className="px-6 md:px-12 lg:px-20 py-20 bg-[#EDE0DC]">
          <AnimatedSection className="max-w-5xl mx-auto text-center">

            <h2 className="text-3xl md:text-4xl font-bold mb-8">
              An Unfinished Story
            </h2>

            <p className="text-base md:text-lg text-[#6B6B6B] leading-relaxed">
              India holds one of the world's richest artistic legacies, and so
              much of it remains untold in fashion.
            </p>

            <p className="mt-6 text-base md:text-lg text-[#6B6B6B] leading-relaxed">
              For centuries, our civilization painted its philosophies onto
              temple walls, wove them into textiles, carved them into stone.
              Every motif carried meaning. Every color had a purpose.
            </p>

            <p className="mt-6 text-base md:text-lg text-[#6B6B6B] leading-relaxed">
              But somewhere along the way, fashion got quieter, chasing
              simplicity and trend cycles.
            </p>

            <p className="mt-8 text-xl md:text-2xl font-semibold text-[#3B2E1E]">
              Fashion was never meant to be quiet or a safe choice.
            </p>

            <p className="mt-3 text-xl md:text-2xl italic text-[#3B2E1E]">
              It is a medium of self-expression. It is Bold and Charismatic.
            </p>

          </AnimatedSection>
        </section>

        {/* =====================================================
            THE QUESTION
        ====================================================== */}

        <section className="px-6 md:px-12 lg:px-20 py-20">
          <AnimatedSection className="max-w-5xl mx-auto">

            <p className="text-base md:text-lg text-[#6B6B6B] leading-relaxed">
              Indian textiles, techniques, and artistic traditions have
              influenced global fashion. Yet our stories, philosophies, and
              cultural meanings behind them often remained unseen.
            </p>

            <p className="mt-8 text-2xl md:text-3xl font-semibold leading-relaxed text-[#3B2E1E]">
              I kept asking myself one question:
            </p>

            <p className="mt-4 text-3xl md:text-4xl italic leading-relaxed text-[#3B2E1E]">
              “If we do not preserve and protect our culture, who will?”
            </p>

            <p className="mt-8 text-base md:text-lg text-[#6B6B6B] leading-relaxed">
              That answer to that question became{" "}
              <strong>Shahu Mumbai.</strong>
            </p>

          </AnimatedSection>
        </section>

        {/* =====================================================
            BIRTH OF SHAHU
        ====================================================== */}

        <section className="grid grid-cols-1 md:grid-cols-2 gap-12 px-6 md:px-12 lg:px-20 py-20 items-center bg-[#EDE0DC]">

          <img
            src={Our}
            alt="The birth of Shahu Mumbai"
            className="rounded-xl h-80 md:h-[460px] w-full object-cover"
            loading="lazy"
          />

          <AnimatedSection>

            <h2 className="text-3xl md:text-4xl font-bold mb-7">
              The Birth of Shahu Mumbai
            </h2>

            <p className="text-base md:text-lg text-[#6B6B6B] leading-relaxed">
              A heritage fashion house dedicated to transforming India's
              forgotten art, ancient stories, lores of creation of life, and
              cultural traditions into modern yet timeless luxury.
            </p>

            <p className="mt-6 text-base md:text-lg text-[#6B6B6B] leading-relaxed">
              Creating a piece of India with the charm and energy of Mumbai
              that travels with you, carrying stories across continents and
              generations.
            </p>

          </AnimatedSection>
        </section>

        {/* =====================================================
            DESIGN PHILOSOPHY
        ====================================================== */}

        <section className="px-6 md:px-12 lg:px-20 py-20">

          <AnimatedSection className="max-w-5xl mx-auto">

            <h2 className="text-3xl md:text-4xl font-bold mb-8">
              Where Every Collection Begins
            </h2>

            <p className="text-base md:text-lg text-[#6B6B6B] leading-relaxed">
              Every collection begins not with a trend forecast, but with
              research and appreciation for culture and history.
            </p>

            <p className="mt-6 text-base md:text-lg text-[#6B6B6B] leading-relaxed">
              Every scarf is a canvas. A canvas where our wonderful designers
              dive into ancient stories, regional crafts, architecture,
              folklore, symbolism, and philosophies that have shaped
              generations.
            </p>

          </AnimatedSection>

        </section>

        {/* =====================================================
            THE CRAFT
        ====================================================== */}

        <section className="grid grid-cols-1 md:grid-cols-2 gap-12 px-6 md:px-12 lg:px-20 py-20 items-center">

          <AnimatedSection>

            <h2 className="text-3xl md:text-4xl font-bold mb-7">
              The Craft
            </h2>

            <p className="text-base md:text-lg text-[#6B6B6B] leading-relaxed">
              Each silk scarf produced at Shahu Mumbai is curated with
              intention and love.
            </p>

            <p className="mt-6 text-base md:text-lg text-[#6B6B6B] leading-relaxed">
              Be it the <strong>Aakar Scarf</strong> depicting Warli Art and
              illusions of leathered texture or the{" "}
              <strong>Shakti Scarf</strong> channeling inner feminine divinity,
              every piece holds a story for you.
            </p>

            <p className="mt-6 text-base md:text-lg text-[#6B6B6B] leading-relaxed">
              Each scarf is hand finished in Mumbai and takes the artisans
              roughly <strong>4 weeks</strong> to complete the entire making
              process after silk production.
            </p>

            <p className="mt-6 text-base md:text-lg text-[#6B6B6B] leading-relaxed">
              The edges are specially hand-rolled by women who inherited
              traditional embroidery and stitching techniques.
            </p>

          </AnimatedSection>

          <img
            src={Craft}
            alt="Craftsmanship at Shahu Mumbai"
            className="rounded-xl h-80 md:h-[460px] w-full object-cover"
            loading="lazy"
          />

        </section>

        {/* =====================================================
            AHIMSA SILK
        ====================================================== */}

        <section className="grid grid-cols-1 md:grid-cols-2 gap-12 px-6 md:px-12 lg:px-20 py-20 items-center bg-[#EDE0DC]">

          <img
            src={Sustainable}
            alt="Ahimsa silk and sustainable craftsmanship"
            className="rounded-xl h-80 md:h-[460px] w-full object-cover"
            loading="lazy"
          />

          <AnimatedSection>

            <h2 className="text-3xl md:text-4xl font-bold mb-7">
              The Silk We Choose
            </h2>

            <p className="text-base md:text-lg text-[#6B6B6B] leading-relaxed">
              We work exclusively in <strong>Ahimsa silk</strong>, a process in
              which silk is harvested only after the silkworm completes its
              natural life cycle.
            </p>

            <p className="mt-6 text-base md:text-lg text-[#6B6B6B] leading-relaxed">
              The entire process of producing Ahimsa silk takes about{" "}
              <strong>45 to 50 days</strong>, which is way slower compared to
              standard silk production.
            </p>

            <p className="mt-6 text-base md:text-lg text-[#6B6B6B] leading-relaxed">
              It's a small act, but to us it's a form of gratitude. The beauty
              we create comes from a world we share with every living thing on
              it, and true luxury should never demand cruelty at its cost.
            </p>

          </AnimatedSection>

        </section>

        {/* =====================================================
            ARTISAN PROCESS
        ====================================================== */}

        <section className="px-6 md:px-12 lg:px-20 py-20">

          <AnimatedSection className="max-w-5xl mx-auto">

            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
              Made Slowly. Made With Intention.
            </h2>

            <p className="text-base md:text-lg text-[#6B6B6B] leading-relaxed text-center">
              At Shahu Mumbai, craftsmanship is not something we rush. Every
              stage of the making process is approached with patience,
              intention, and respect for the people who bring each piece to
              life.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-14">

              <div className="text-center">

                <div className="text-4xl font-semibold mb-4">
                  45–50
                </div>

                <h3 className="text-xl font-semibold mb-3">
                  Days of Silk Production
                </h3>

                <p className="text-[#6B6B6B] leading-relaxed">
                  Ahimsa silk follows a slower and more considered process.
                </p>

              </div>

              <div className="text-center">

                <div className="text-4xl font-semibold mb-4">
                  4
                </div>

                <h3 className="text-xl font-semibold mb-3">
                  Weeks of Hand Finishing
                </h3>

                <p className="text-[#6B6B6B] leading-relaxed">
                  Each scarf is carefully finished by artisans in Mumbai.
                </p>

              </div>

              <div className="text-center">

                <div className="text-4xl font-semibold mb-4">
                  1
                </div>

                <h3 className="text-xl font-semibold mb-3">
                  Story in Every Piece
                </h3>

                <p className="text-[#6B6B6B] leading-relaxed">
                  Every design carries a piece of India's artistic heritage.
                </p>

              </div>

            </div>

          </AnimatedSection>

        </section>

        {/* =====================================================
            CLOSING STATEMENT
        ====================================================== */}

        <section className="relative min-h-[500px] flex items-center justify-center text-center text-white">

          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(${OurStory})`,
            }}
          />

          <div className="absolute inset-0 bg-black/55" />

          <AnimatedSection className="relative z-10 max-w-4xl px-6">

            <h2 className="text-3xl md:text-5xl font-semibold mb-8">
              This Is Only the Beginning.
            </h2>

            <p className="text-lg md:text-xl leading-relaxed text-[#F6E7D8]">
              Creating a piece of India with the charm and energy of Mumbai
              that travels with you, carrying stories across continents and
              generations.
            </p>

            <p className="mt-8 text-2xl md:text-3xl font-semibold">
              Welcome to Shahu Mumbai.
            </p>

            <p className="mt-4 text-xl md:text-2xl italic text-[#F6E7D8]">
              Our world, curated to be yours.
            </p>

          </AnimatedSection>

        </section>

      </div>
    </Layout>
  );
}

export default About;